'use client';

import { useState } from 'react';
import { Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/ui';
import { cn } from '@/lib/cn';
import { formatDurationClock } from '@/lib/format';
import { pooledDialerApi } from '@/lib/api/pooled-dialer';
import { usePoolMembership } from '@/providers/pool-membership';

// Mirrors the server's WRAPUP_EXTEND_WINDOW_MS — the button shows only when a
// press would be accepted.
const EXTEND_WINDOW_SEC = 30;

/**
 * The wrap-up time-box: counts down to the server's park-idle deadline and
 * offers +10s inside the final window. The server owns the clock — this only
 * renders the deadline it was given and asks for more time; when it hits zero
 * the next snapshot moves the member to the idle screen (card 455), where
 * re-joining the queue is their explicit choice.
 */
export function WrapupCountdown({
  campaignId,
  deadline,
  now,
  compact = false,
}: {
  campaignId: string;
  /** Parsed wrapupDeadline (ms epoch). */
  deadline: number;
  now: number;
  compact?: boolean;
}) {
  const { refresh } = usePoolMembership();
  const [extending, setExtending] = useState(false);
  const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
  const urgent = remaining <= 10;

  const extend = () => {
    setExtending(true);
    pooledDialerApi
      .extendWrapup(campaignId)
      .then(() => {
        refresh();
      })
      .catch((err: unknown) => {
        toast.error(err instanceof Error ? err.message : 'Failed to extend wrap-up');
      })
      .finally(() => {
        setExtending(false);
      });
  };

  if (compact) {
    return (
      <>
        <span className={cn('shrink-0 text-sm tabular-nums', urgent ? 'text-danger' : 'text-warning')}>
          {formatDurationClock(remaining)}
        </span>
        {remaining <= EXTEND_WINDOW_SEC ? (
          <Button size="sm" variant="secondary" icon={Plus} loading={extending} className="shrink-0" onClick={extend}>
            10s
          </Button>
        ) : null}
      </>
    );
  }

  return (
    <div className="mt-4">
      {remaining > 0 ? (
        <>
          <p className={cn('text-2xl font-semibold tabular-nums', urgent ? 'text-danger' : 'text-warning')}>
            {formatDurationClock(remaining)}
          </p>
          <p className="mt-1 text-xs text-zinc-400">
            When this hits zero you go <span className="text-zinc-300">idle</span> — press{' '}
            <span className="text-zinc-300">Next</span> to stay in line.
          </p>
          {remaining <= EXTEND_WINDOW_SEC ? (
            <Button icon={Plus} variant="secondary" className="mt-3" loading={extending} onClick={extend}>
              10 seconds
            </Button>
          ) : null}
        </>
      ) : (
        // The server parks an expired wrap-up in idle (card 455) — never back
        // into the queue, so don't promise that. If this line stays on screen
        // beyond a beat, the next snapshot is late, not the agent.
        <p className="text-sm text-warning">Time&apos;s up — taking you off the line…</p>
      )}
    </div>
  );
}
